/**
 * 活动签到时间窗：与后端 EnsureActivityMemberCheckInAllowed 口径一致（face UTC，不做 +8）。
 * - 开放：EventDate（face UTC）− checkInEarlyMinutes
 * - 截止：min(活动结束, 开始 + checkInLateMinutes)；checkInLateMinutes 为空则不限制延后（至活动结束）
 */



export type ActivityCheckInWindowInput = {

  eventDate: string

  eventEndDate?: string | null

  checkInEarlyMinutes?: number | null

  /** null/undefined = 不限制开始后签到时限（至活动结束） */

  checkInLateMinutes?: number | null

}



const CHINA_OFFSET_MS = 8 * 60 * 60 * 1000



function normalizeEarlyMinutes(raw: number | null | undefined): number {

  const n = Number(raw ?? 0)

  if (!Number.isFinite(n) || n < 0) return 0

  return Math.min(Math.floor(n), 7 * 24 * 60)

}



function normalizeLateMinutes(raw: number | null | undefined): number | null {

  if (raw == null || raw === undefined) return null

  const n = Number(raw)

  if (!Number.isFinite(n) || n < 0) return null

  return Math.min(Math.floor(n), 7 * 24 * 60)

}



/** 解析管理端/接口下发的时刻为 UTC 毫秒（与后端 ActivityInstantAsUtc + ParseTenantUiInstant 对齐）。 */

export function parseActivityInstantToUtcMs(raw: string): number | null {

  const s = String(raw || '').trim()

  if (!s) return null

  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {

    const d = Date.parse(`${s}T00:00:00Z`)

    return Number.isNaN(d) ? null : d

  }

  const normalized = s.includes('T') ? s : s.replace(' ', 'T')

  const withZ =

    /[zZ]$/.test(normalized) || /[+-]\d{2}:?\d{2}$/.test(normalized)

      ? normalized

      : `${normalized}Z`

  const t = Date.parse(withZ)

  return Number.isNaN(t) ? null : t

}

/** 中国墙钟 → face UTC 毫秒（与后端 TenantUiNow 同一套比较口径，不做 +8）。 */
export function tenantUiNowMs(nowMs: number = Date.now()): number {
  const cn = new Date(nowMs + CHINA_OFFSET_MS)
  return Date.UTC(
    cn.getUTCFullYear(),
    cn.getUTCMonth(),
    cn.getUTCDate(),
    cn.getUTCHours(),
    cn.getUTCMinutes(),
    cn.getUTCSeconds(),
    cn.getUTCMilliseconds(),
  )
}

/** face UTC 毫秒 → yyyy-MM-dd HH:mm 展示标签（不再 +8）。 */
export function formatTenantUiInstantLabel(ms: number): string {
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}`
}

/** 管理端 yyyy-MM-dd / yyyy-MM-dd HH:mm → face UTC ISO（不做本地时区换算）。 */
export function tenantUiInstantToApiIso(raw: string): string {
  const t = raw.trim()
  if (!t) return t
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return `${t}T00:00:00.000Z`
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}$/.test(t)) return `${t.replace(' ', 'T')}:00.000Z`
  if (/[zZ]$/.test(t) || /[+-]\d{2}:?\d{2}$/.test(t)) return t
  const normalized = t.includes('T') ? t : t.replace(' ', 'T')
  return /[zZ]$/.test(normalized) ? normalized : `${normalized}Z`
}

/** face UTC 自然日结束（23:59:59.999）。 */
export function tenantUiEndOfDayMs(startMs: number): number {
  const d = new Date(startMs)
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), 23, 59, 59, 999)
}

function tenantUiCalendarPartsFromFaceMs(ms: number) {
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    y: d.getUTCFullYear(),
    month: pad(d.getUTCMonth() + 1),
    day: pad(d.getUTCDate()),
    hour: pad(d.getUTCHours()),
    minute: pad(d.getUTCMinutes()),
  }
}

function chinaCalendarPartsFromUtcMs(ms: number) {
  const cn = new Date(ms + CHINA_OFFSET_MS)
  const pad = (n: number) => String(n).padStart(2, '0')
  return {
    y: cn.getUTCFullYear(),
    month: pad(cn.getUTCMonth() + 1),
    day: pad(cn.getUTCDate()),
    hour: pad(cn.getUTCHours()),
    minute: pad(cn.getUTCMinutes()),
  }
}

/** 管理端列表/详情：face UTC 展示（库内数字即中国墙钟，不做 +8）。 */
export function formatActivityInstantChinaDisplay(raw: string, fallback = '—'): string {
  const s = String(raw || '').trim()
  if (!s) return fallback
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s
  const ms = parseActivityInstantToUtcMs(s)
  if (ms == null) return (s.split('T')[0] || s).trim() || fallback
  const p = tenantUiCalendarPartsFromFaceMs(ms)
  const ymd = `${p.y}-${p.month}-${p.day}`
  if (!s.includes('T') && !/\d{1,2}:\d{2}/.test(s)) return ymd
  return `${ymd} ${p.hour}:${p.minute}`
}

/** 活动签到/签退记录时刻展示（face UTC，与库内数字一致，不做 +8）。 */
export function formatActivityAttendanceInstantDisplay(raw: string, fallback = '—'): string {
  return formatActivityInstantChinaDisplay(raw, fallback)
}

/** 仅时刻 HH:mm（face UTC）。 */
export function formatActivityAttendanceTimeOnly(raw: string, fallback = '—'): string {
  const display = formatActivityAttendanceInstantDisplay(raw, '')
  if (!display) return fallback
  const parts = display.split(' ')
  return parts.length >= 2 ? parts.slice(1).join(' ') : display
}

/** face UTC 日期 yyyy-MM-dd（用于「今日已签到」等判断）。 */
export function activityAttendanceFaceUtcYmd(raw: string): string {
  const display = formatActivityAttendanceInstantDisplay(raw, '')
  return (display.split(' ')[0] || '').trim()
}

/** 当前中国自然日 yyyy-MM-dd（与 tenantUiNowMs 对齐）。 */
export function tenantUiTodayYmd(nowMs: number = Date.now()): string {
  const ms = tenantUiNowMs(nowMs)
  const d = new Date(ms)
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

/** 订单 createdAt/paidAt：UTC 时刻 → 中国时区 yyyy-MM-dd HH:mm（兼容旧 API 的 yyyy-MM-dd HH:mm UTC 时钟与 ISO）。 */
export function formatOrderInstantChinaDisplay(raw: string, fallback = '—'): string {
  const s = String(raw || '').trim()
  if (!s) return fallback
  if (/^\d{4}-\d{2}-\d{2} \d{1,2}:\d{2}$/.test(s)) {
    const ms = parseActivityInstantToUtcMs(`${s.replace(' ', 'T')}:00Z`)
    if (ms != null) {
      const p = chinaCalendarPartsFromUtcMs(ms)
      return `${p.y}-${p.month}-${p.day} ${p.hour}:${p.minute}`
    }
  }
  const ms = parseActivityInstantToUtcMs(s)
  if (ms == null) return s || fallback
  const p = chinaCalendarPartsFromUtcMs(ms)
  return `${p.y}-${p.month}-${p.day} ${p.hour}:${p.minute}`
}

/** 管理端表单回显：拆为 face UTC 日期与时间（与 ParseTenantUiInstant / FormatInstantForAdminApi 对齐）。 */
export function splitActivityInstantChina(val: string | null | undefined): { day: string; time: string } {
  const s = String(val || '').trim()
  if (!s) return { day: '', time: '' }
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return { day: s, time: '' }
  const ms = parseActivityInstantToUtcMs(s)
  if (ms == null) {
    const normalized = s.replace('T', ' ')
    const [day = '', time = ''] = normalized.split(' ')
    return { day, time: time.slice(0, 5) }
  }
  const p = tenantUiCalendarPartsFromFaceMs(ms)
  return { day: `${p.y}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` }
}

function activityCheckInClosesAtUtcMs(input: ActivityCheckInWindowInput): number | null {

  const startMs = parseActivityInstantToUtcMs(input.eventDate)

  if (startMs == null) return null



  const endRaw = String(input.eventEndDate ?? '').trim()

  if (endRaw) {

    const endMs = parseActivityInstantToUtcMs(endRaw)

    if (endMs == null) return null

    const endDate = new Date(endMs)

    const isDateOnly =

      /^\d{4}-\d{2}-\d{2}$/.test(endRaw) ||

      (endDate.getUTCHours() === 0 &&

        endDate.getUTCMinutes() === 0 &&

        endDate.getUTCSeconds() === 0 &&

        endDate.getUTCMilliseconds() === 0)

    if (isDateOnly) return endMs + 24 * 60 * 60 * 1000 - 1

    return endMs

  }

  return tenantUiEndOfDayMs(startMs)

}



export function getActivityCheckInWindow(input: ActivityCheckInWindowInput): {

  opensAtMs: number

  closesAtMs: number

} | null {

  const startMs = parseActivityInstantToUtcMs(input.eventDate)

  const lifecycleEndMs = activityCheckInClosesAtUtcMs(input)

  if (startMs == null || lifecycleEndMs == null) return null

  const early = normalizeEarlyMinutes(input.checkInEarlyMinutes)

  const late = normalizeLateMinutes(input.checkInLateMinutes)

  let closesMs = lifecycleEndMs

  if (late != null) {

    const lateCap = startMs + late * 60 * 1000

    if (lateCap < closesMs) closesMs = lateCap

  }

  return {

    opensAtMs: startMs - early * 60 * 1000,

    closesAtMs: closesMs,

  }

}



export function isActivityCheckInAllowedNow(

  input: ActivityCheckInWindowInput,

  nowMs: number = tenantUiNowMs(),

): boolean {

  const w = getActivityCheckInWindow(input)

  if (!w) return true

  return nowMs >= w.opensAtMs && nowMs <= w.closesAtMs

}



/** 不可签到时的简短提示；可签到时返回 null。 */

export function activityCheckInBlockedMessage(

  input: ActivityCheckInWindowInput,

  nowMs: number = tenantUiNowMs(),

): string | null {

  const w = getActivityCheckInWindow(input)

  if (!w) return null

  if (nowMs < w.opensAtMs) {

    const early = normalizeEarlyMinutes(input.checkInEarlyMinutes)

    const label = formatTenantUiInstantLabel(w.opensAtMs)

    if (early > 0) return `签到尚未开放，可于 ${label} 起签到（活动开始前 ${early} 分钟）`

    return `签到尚未开放，请于活动开始时间（${label}）后再试`

  }

  if (nowMs > w.closesAtMs) {

    const late = normalizeLateMinutes(input.checkInLateMinutes)

    if (late != null && late > 0) {

      const label = formatTenantUiInstantLabel(w.closesAtMs)

      return `签到已结束（活动开始后 ${late} 分钟内可签到，截止 ${label}）`

    }

    return '签到已结束'

  }

  return null

}



/** @deprecated 使用 isActivityCheckInAllowedNow */

export function isActivityCheckInAllowedByDate(

  activityDateRaw: string,

  checkInEarlyMinutes?: number | null,

  eventEndDate?: string | null,

): boolean {

  return isActivityCheckInAllowedNow({

    eventDate: activityDateRaw,

    eventEndDate,

    checkInEarlyMinutes,

  })

}


